import type {
  SupportedLanguage,
  ApiResponse,
  ChatSession,
  ChatSessionWithMessages,
  SendMessageInput,
  SendMessageResponse,
  BirthProfile,
  CreateBirthProfileInput,
  KundaliChart,
  User,
  PredictionType,
  PredictionHistoryResponse,
  PredictionResponse,
  Report,
  ReportType,
  CompatibilityMatch,
  PartnerDetails,
  FullDoshaAnalysis,
  ManglikDoshaAnalysis,
  KaalSarpDoshaAnalysis,
  SadheSatiAnalysis,
  Remedy,
  RemediesPackage,
  GemstoneRemedy,
  MantraRemedy,
  CharityRemedy,
  BehavioralRemedy,
  SpiritualRemedy,
  SharedInsight,
  ShareType,
  PublicShareView,
  CreateShareInput,
  RegisterInput,
  LoginInput,
  AuthResponse,
  UserResponse,
  ToolCalculateInput,
  ToolCalculateResponse,
  AcquisitionInput,
} from '@kundaliai/shared';

// Payment types
export type CreditPackType = 'pack_10' | 'pack_50' | 'pack_150' | 'free_signup';
export type PaymentType = 'subscription' | 'credits' | 'lifetime';
export type PaymentStatus = 'pending' | 'success' | 'failed' | 'refunded';
export type BillingCycle = 'monthly' | 'yearly';

export interface CreatePaymentInput {
  type: PaymentType;
  tier?: 'basic' | 'premium' | 'lifetime';
  billingCycle?: BillingCycle;
  creditPack?: CreditPackType;
}

export interface CreatePaymentResponse {
  orderId: string;
  amount: number;
  currency: string;
  keyId: string;
}

export interface VerifyPaymentInput {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

export interface VerifyPaymentResponse {
  merchantTransactionId: string;
  razorpayPaymentId: string;
  type: PaymentType;
  amount: number;
  status: PaymentStatus;
}

export interface PaymentStatusResponse {
  merchantTransactionId: string;
  razorpayPaymentId: string | null;
  type: PaymentType;
  amount: number;
  status: PaymentStatus;
  paymentMethod: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TransactionHistoryItem {
  id: number;
  merchantTransactionId: string;
  type: PaymentType;
  amount: number;
  status: PaymentStatus;
  paymentMethod: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface CreditPurchase {
  id: number;
  credits: number;
  creditsRemaining: number;
  packType: CreditPackType;
  purchasedAt: string;
  expiresAt: string;
  status: 'active' | 'expired' | 'exhausted';
}

export interface CreditBalance {
  total: number;
  available: number;
  expiringSoon: number;
  purchases: CreditPurchase[];
}

export interface SubscriptionInfo {
  tier: 'free' | 'basic' | 'premium' | 'lifetime';
  status: 'active' | 'cancelled' | 'expired' | 'trial';
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

export interface CreditPackInfo {
  id: string;
  credits: number;
  price: number;
  label: string;
  priceDisplay: string;
  savings?: string;
}

export interface CreateGuestInput {
  name: string;
  dateOfBirth: string;
  timeOfBirth?: string;
  placeOfBirth?: string;
  latitude?: number;
  longitude?: number;
  gender?: 'male' | 'female' | 'other';
  maritalStatus?: 'married' | 'unmarried';
  preferredLanguage?: 'en' | 'hi' | 'mr';
  deviceFingerprint?: string;
  acquisition?: AcquisitionInput;
}

export interface GuestSessionResponse {
  guestId: string;
  token: string;
  creditsRemaining: number;
  name: string;
}

export interface GuestMeResponse {
  guestId: string;
  name: string;
  creditsRemaining: number;
  // Birth details the guest gave when the session was created, so returning
  // visitors don't get asked for them again. Null on older sessions.
  dateOfBirth?: string | null;
  timeOfBirth?: string | null;
  placeOfBirth?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export type GuestClaimResponse =
  | { claimed: true; birthProfileId: number }
  | { claimed: false; reason: 'already-has-profile' | 'no-session' };

import { resolveAcquisition } from '@/lib/acquisition';

/**
 * Attach first-touch attribution to the calls that create an identity.
 *
 * Done here rather than at each call site so no signup path can silently miss
 * it — there are three (guest session, email register, Google) and they're in
 * different files.
 */
function withAcquisition<T extends object>(input: T): T & { acquisition?: AcquisitionInput } {
  const acquisition = resolveAcquisition();
  return acquisition ? { ...input, acquisition } : input;
}

const API_URL = import.meta.env.VITE_API_URL || '';

class ApiClient {
  private baseUrl: string;
  private getToken: (() => Promise<string | null>) | null = null;
  private guestToken: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  setTokenGetter(getter: () => Promise<string | null>) {
    this.getToken = getter;
  }

  setGuestToken(token: string | null) {
    this.guestToken = token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.getToken) {
      const userToken = await this.getToken();
      const authToken = userToken || this.guestToken;
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
    } else if (this.guestToken) {
      headers['Authorization'] = `Bearer ${this.guestToken}`;
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    const json = await response.json();

    if (!response.ok || !json.success) {
      return {
        success: false,
        error: json.error || { code: 'UNKNOWN', message: 'An error occurred' },
      };
    }

    // API returns { success: true, data: T }, extract the data
    return { success: true, data: json.data as T };
  }

  // Auth endpoints (no auth header needed for these)
  async register(input: RegisterInput): Promise<AuthResponse> {
    const response = await fetch(`${this.baseUrl}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(withAcquisition(input)),
    });
    const json = await response.json();
    if (!response.ok) {
      throw new Error(json.error?.message || 'Registration failed');
    }
    return json;
  }

  async login(input: LoginInput): Promise<AuthResponse> {
    const response = await fetch(`${this.baseUrl}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    const json = await response.json();
    if (!response.ok) {
      throw new Error(json.error?.message || 'Login failed');
    }
    return json;
  }

  async refreshToken(refreshToken: string): Promise<{ accessToken: string; refreshToken: string; expiresIn: number }> {
    const response = await fetch(`${this.baseUrl}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    const json = await response.json();
    if (!response.ok) {
      throw new Error(json.error?.message || 'Token refresh failed');
    }
    return json;
  }

  async logout(refreshToken?: string): Promise<void> {
    await fetch(`${this.baseUrl}/api/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
  }

  async socialLogin(provider: string, idToken: string): Promise<AuthResponse & { isNewUser: boolean }> {
    const response = await fetch(`${this.baseUrl}/api/auth/social/${provider}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(withAcquisition({ idToken })),
    });
    const json = await response.json();
    if (!response.ok) {
      throw new Error(json.error?.message || 'Social login failed');
    }
    return json;
  }

  async createGuestSession(input: CreateGuestInput): Promise<ApiResponse<GuestSessionResponse>> {
    input = withAcquisition(input);
    return this.request('/api/guest/session', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  /**
   * Public tool calculators. Unauthenticated by design — the SEO landing pages
   * must give a real answer without a signup or a guest session.
   */
  /** Today's panchang. Public, unauthenticated, free — no account required. */
  async getTodayPanchang(params?: { lat?: number; lon?: number; tz?: string; date?: string }) {
    const q = new URLSearchParams();
    if (params?.lat !== undefined) q.set('lat', String(params.lat));
    if (params?.lon !== undefined) q.set('lon', String(params.lon));
    if (params?.tz) q.set('tz', params.tz);
    if (params?.date) q.set('date', params.date);
    const qs = q.toString();
    return this.request<any>(`/api/panchang/today${qs ? `?${qs}` : ''}`);
  }

  /** Today's rashifal for all twelve signs. Public, unauthenticated, free. */
  async getTodayRashifal(params?: { date?: string }) {
    const qs = params?.date ? `?date=${encodeURIComponent(params.date)}` : '';
    return this.request<any>(`/api/rashifal/today${qs}`);
  }

  /** Live block for a nakshatra reference page: today's tara bala, Moon/Sun passages, lord position. Public, free. */
  async getNakshatraToday(slug: string, params?: { date?: string }) {
    const qs = params?.date ? `?date=${encodeURIComponent(params.date)}` : '';
    return this.request<any>(`/api/nakshatra/${encodeURIComponent(slug)}/today${qs}`);
  }

  async calculateTool(input: ToolCalculateInput): Promise<ApiResponse<ToolCalculateResponse>> {
    return this.request('/api/tools/calculate', { method: 'POST', body: JSON.stringify(input) });
  }

  async getGuestMe(): Promise<ApiResponse<GuestMeResponse>> {
    return this.request('/api/guest/session/me');
  }

  /**
   * Hand a guest session's birth details to the account the visitor has just
   * signed into. Authenticated as the user (the Authorization header carries
   * their token); the guest token is proved in the body because the two
   * credentials cannot share one header.
   */
  async claimGuestSession(guestToken: string): Promise<ApiResponse<GuestClaimResponse>> {
    return this.request('/api/guest/claim', {
      method: 'POST',
      body: JSON.stringify({ guestToken }),
    });
  }

  async getMe(): Promise<UserResponse> {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (this.getToken) {
      const token = await this.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    const response = await fetch(`${this.baseUrl}/api/auth/me`, { headers });
    const json = await response.json();
    if (!response.ok) {
      throw new Error(json.error?.message || 'Failed to get user');
    }
    return json;
  }

  // Chat endpoints
  async sendMessage(input: SendMessageInput): Promise<ApiResponse<SendMessageResponse>> {
    return this.request('/api/chat/message', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async getChatSessions(): Promise<ApiResponse<ChatSession[]>> {
    return this.request('/api/chat/sessions');
  }

  async getChatSession(sessionId: string): Promise<ApiResponse<ChatSessionWithMessages>> {
    return this.request(`/api/chat/sessions/${sessionId}`);
  }

  async createChatSession(): Promise<ApiResponse<ChatSession>> {
    return this.request('/api/chat/sessions', { method: 'POST' });
  }

  async deleteChatSession(sessionId: string): Promise<ApiResponse<void>> {
    return this.request(`/api/chat/sessions/${sessionId}`, { method: 'DELETE' });
  }

  // User endpoints
  async getProfile(): Promise<ApiResponse<User & { birthProfile?: BirthProfile }>> {
    return this.request('/api/user/profile');
  }

  /** Update the account fields a user may change about themselves. Name only. */
  async updateProfile(input: { name: string | null }): Promise<ApiResponse<User>> {
    return this.request('/api/user/profile', {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  async createBirthProfile(input: CreateBirthProfileInput): Promise<ApiResponse<BirthProfile>> {
    return this.request('/api/user/birth-profile', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateBirthProfile(
    id: number,
    input: Partial<CreateBirthProfileInput>
  ): Promise<ApiResponse<BirthProfile>> {
    return this.request(`/api/user/birth-profile/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  async getBirthProfiles(): Promise<ApiResponse<BirthProfile[]>> {
    return this.request('/api/user/birth-profiles');
  }

  // Kundali endpoints
  async generateKundali(birthProfileId: number): Promise<ApiResponse<KundaliChart>> {
    return this.request('/api/kundali/generate', {
      method: 'POST',
      body: JSON.stringify({ birthProfileId }),
    });
  }

  async generateGuestKundali(): Promise<ApiResponse<KundaliChart>> {
    return this.request('/api/kundali/generate', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async regenerateGuestKundali(): Promise<ApiResponse<KundaliChart>> {
    return this.request('/api/kundali/regenerate/0', {
      method: 'POST',
    });
  }

  async getKundali(id: number): Promise<ApiResponse<KundaliChart>> {
    return this.request(`/api/kundali/${id}`);
  }

  async getUserKundalis(): Promise<ApiResponse<KundaliChart[]>> {
    return this.request('/api/kundali/user');
  }

  async regenerateKundali(birthProfileId: number): Promise<ApiResponse<KundaliChart>> {
    return this.request(`/api/kundali/regenerate/${birthProfileId}`, {
      method: 'POST',
    });
  }

  // Prediction endpoints
  async getDailyPrediction(date?: string, language?: SupportedLanguage): Promise<ApiResponse<PredictionResponse>> {
    const params = new URLSearchParams();
    if (date) params.set('date', date);
    if (language) params.set('language', language);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/api/predictions/daily${qs}`);
  }

  async getWeeklyPrediction(date?: string, language?: SupportedLanguage): Promise<ApiResponse<PredictionResponse>> {
    const params = new URLSearchParams();
    if (date) params.set('date', date);
    if (language) params.set('language', language);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/api/predictions/weekly${qs}`);
  }

  async getMonthlyPrediction(date?: string, language?: SupportedLanguage): Promise<ApiResponse<PredictionResponse>> {
    const params = new URLSearchParams();
    if (date) params.set('date', date);
    if (language) params.set('language', language);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/api/predictions/monthly${qs}`);
  }

  async getPredictionHistory(
    page = 1,
    limit = 10,
    type?: PredictionType
  ): Promise<ApiResponse<PredictionHistoryResponse>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (type) params.set('type', type);
    return this.request(`/api/predictions/history?${params.toString()}`);
  }

  async regeneratePrediction(type: PredictionType, language?: SupportedLanguage): Promise<ApiResponse<PredictionResponse>> {
    return this.request(`/api/predictions/regenerate/${type}`, {
      method: 'POST',
      body: JSON.stringify({ language }),
    });
  }

  // Report endpoints
  async getReport(type: ReportType, language?: SupportedLanguage): Promise<ApiResponse<{ report: Report; isNew: boolean }>> {
    const qs = language ? `?language=${language}` : '';
    return this.request(`/api/reports/${type}${qs}`);
  }

  async generateReport(type: ReportType, language?: SupportedLanguage): Promise<ApiResponse<{ report: Report; isNew: boolean }>> {
    return this.request('/api/reports/generate', {
      method: 'POST',
      body: JSON.stringify({ type, language }),
    });
  }

  async getReportsHistory(
    page = 1,
    limit = 10,
    type?: ReportType
  ): Promise<ApiResponse<{ reports: Report[]; total: number; page: number; limit: number; hasMore: boolean }>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (type) params.set('type', type);
    return this.request(`/api/reports?${params.toString()}`);
  }

  async getAvailableReports(): Promise<ApiResponse<{
    hasKundali: boolean;
    reports: Array<{ type: ReportType; label: string; generated: boolean; generatedAt: Date | null }>;
  }>> {
    return this.request('/api/reports/available');
  }

  // Compatibility endpoints
  async checkCompatibility(partner: PartnerDetails, language?: SupportedLanguage): Promise<ApiResponse<{ match: CompatibilityMatch; isNew: boolean }>> {
    return this.request('/api/compatibility/check', {
      method: 'POST',
      body: JSON.stringify({ partner, language }),
    });
  }

  async getCompatibilityHistory(
    page = 1,
    limit = 10
  ): Promise<ApiResponse<{ matches: CompatibilityMatch[]; total: number; page: number; limit: number; hasMore: boolean }>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    return this.request(`/api/compatibility/history?${params.toString()}`);
  }

  async getCompatibilityMatch(id: number): Promise<ApiResponse<{ match: CompatibilityMatch }>> {
    return this.request(`/api/compatibility/${id}`);
  }

  async deleteCompatibilityMatch(id: number): Promise<ApiResponse<{ deleted: boolean }>> {
    return this.request(`/api/compatibility/${id}`, { method: 'DELETE' });
  }

  // Dosha endpoints
  async getDoshaAnalysis(): Promise<ApiResponse<{ analysis: FullDoshaAnalysis; analyzedAt: Date; isNew: boolean }>> {
    return this.request('/api/dosha');
  }

  async regenerateDoshaAnalysis(): Promise<ApiResponse<{ analysis: FullDoshaAnalysis; analyzedAt: Date; isNew: boolean }>> {
    return this.request('/api/dosha/regenerate', { method: 'POST' });
  }

  async getManglikDosha(): Promise<ApiResponse<{ manglik: ManglikDoshaAnalysis }>> {
    return this.request('/api/dosha/manglik');
  }

  async getKaalSarpDosha(): Promise<ApiResponse<{ kaalSarp: KaalSarpDoshaAnalysis }>> {
    return this.request('/api/dosha/kaal-sarp');
  }

  async getSadheSatiAnalysis(): Promise<ApiResponse<{ sadheSati: SadheSatiAnalysis }>> {
    return this.request('/api/dosha/sadhe-sati');
  }

  // Remedies endpoints
  async getRemedies(category?: string): Promise<ApiResponse<{
    remedies: Remedy[];
    package: RemediesPackage;
    generatedAt: Date;
    isNew: boolean;
  }>> {
    const params = category ? `?category=${category}` : '';
    return this.request(`/api/remedies${params}`);
  }

  async regenerateRemedies(): Promise<ApiResponse<{
    remedies: Remedy[];
    package: RemediesPackage;
    generatedAt: Date;
    isNew: boolean;
  }>> {
    return this.request('/api/remedies/regenerate', { method: 'POST' });
  }

  async getGemstoneRemedies(): Promise<ApiResponse<{
    gemstones: GemstoneRemedy[];
    disclaimer: string;
  }>> {
    return this.request('/api/remedies/gemstones');
  }

  async getMantraRemedies(): Promise<ApiResponse<{ mantras: MantraRemedy[] }>> {
    return this.request('/api/remedies/mantras');
  }

  async getCharityRemedies(): Promise<ApiResponse<{ charities: CharityRemedy[] }>> {
    return this.request('/api/remedies/charities');
  }

  async getSpiritualRemedies(): Promise<ApiResponse<{
    behavioral: BehavioralRemedy[];
    spiritual: SpiritualRemedy[];
  }>> {
    return this.request('/api/remedies/spiritual');
  }

  // Sharing endpoints
  async createShare(input: CreateShareInput): Promise<ApiResponse<{
    share: SharedInsight;
    shareUrl: string;
  }>> {
    return this.request('/api/share', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async getShare(shareId: string): Promise<ApiResponse<PublicShareView>> {
    return this.request(`/api/share/${shareId}`);
  }

  async getMyShares(
    page = 1,
    limit = 10,
    type?: ShareType
  ): Promise<ApiResponse<{
    shares: Array<SharedInsight & { shareUrl: string }>;
    page: number;
    limit: number;
    hasMore: boolean;
  }>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (type) params.set('type', type);
    return this.request(`/api/share?${params.toString()}`);
  }

  async deleteShare(shareId: string): Promise<ApiResponse<{ deleted: boolean }>> {
    return this.request(`/api/share/${shareId}`, { method: 'DELETE' });
  }

  async getShareStats(shareId: string): Promise<ApiResponse<{
    shareId: string;
    viewCount: number;
    createdAt: Date;
    expiresAt: Date | null;
    isExpired: boolean;
    shareUrl: string;
  }>> {
    return this.request(`/api/share/${shareId}/stats`);
  }

  // Payment endpoints
  async createPaymentOrder(input: CreatePaymentInput): Promise<ApiResponse<CreatePaymentResponse>> {
    return this.request('/api/payments/create-order', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async verifyPayment(input: VerifyPaymentInput): Promise<ApiResponse<VerifyPaymentResponse>> {
    return this.request('/api/payments/verify', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async getPaymentStatus(txnId: string): Promise<ApiResponse<PaymentStatusResponse>> {
    return this.request(`/api/payments/status/${txnId}`);
  }

  async getPaymentHistory(): Promise<ApiResponse<TransactionHistoryItem[]>> {
    return this.request('/api/payments/history');
  }

  async getCreditPacks(): Promise<ApiResponse<CreditPackInfo[]>> {
    return this.request('/api/payments/credit-packs');
  }

  async getCreditsBalance(): Promise<ApiResponse<CreditBalance>> {
    return this.request('/api/payments/credits');
  }

  async getSubscription(): Promise<ApiResponse<SubscriptionInfo>> {
    return this.request('/api/payments/subscription');
  }

  // Notification endpoints
  async getVapidPublicKey(): Promise<ApiResponse<{ publicKey: string }>> {
    return this.request('/api/notifications/vapid-public-key');
  }

  async subscribePush(subscription: {
    endpoint: string;
    keys: { p256dh: string; auth: string };
  }): Promise<ApiResponse<{ subscribed: boolean }>> {
    return this.request('/api/notifications/subscribe', {
      method: 'POST',
      body: JSON.stringify({ subscription }),
    });
  }

  async unsubscribePush(endpoint: string): Promise<ApiResponse<{ unsubscribed: boolean }>> {
    return this.request('/api/notifications/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint }),
    });
  }

  async getUnreadNotificationCount(): Promise<ApiResponse<{ count: number }>> {
    return this.request('/api/notifications/unread-count');
  }

  async getNotifications(page = 1, limit = 20): Promise<ApiResponse<any>> {
    return this.request(`/api/notifications?page=${page}&limit=${limit}`);
  }

  async markNotificationRead(id: number): Promise<ApiResponse<{ read: boolean }>> {
    return this.request(`/api/notifications/${id}/read`, { method: 'PATCH' });
  }

  async markAllNotificationsRead(): Promise<ApiResponse<{ read: boolean }>> {
    return this.request('/api/notifications/mark-all-read', { method: 'POST' });
  }
}

export const api = new ApiClient(API_URL);
